import type { AxiosResponse, AxiosRequestConfig } from 'axios'
import type {
  DocumentItem,
  DownloadFileParameters,
  QueryDocumentDetailModel,
  QueryDocumentDetailParameters,
  SaveDocumentDetailParameters,
  TransactionSequence,
  UploadFileModel,
  UploadFileParameter,
  QueryProductDocumentRelationParameters,
  QueryProductDocumentRelationModel,
  UpdateProductDocumentRelationParameters,
  SaveOcrFileDataParameter,
  GetOcrFileDataParameter,
  QueryOCRFileListModel,
  UpdateOcrFileDataParameter,
} from './model/documentModel'
import type { PostDocumentAddItemsType } from './model/documentAddModel'
import type { BasicResult } from '../model/resultModel'

import { bffHTTP } from '@/utils/http/axios'
import { handleAPIException } from '@/utils/exception'
import { getMessage } from '@/utils/message'
import { errorMessage } from '@/utils/util'
import { encodeUnicodeToBase64 } from '@/utils'

enum API {
  DOCUMENT_ADD = '/products/documents',
}

/**
 * @description ドキュメント編集初期表示API
 * @param parameters
 * @returns
 */
export async function queryDocumentDetail({
  folderID,
  productID,
  documentID,
}: QueryDocumentDetailParameters): Promise<DocumentItem | undefined> {
  try {
    const response: AxiosResponse<QueryDocumentDetailModel, any> = await bffHTTP({
      method: 'GET',
      url: `/folders/${folderID}/products/${productID}/documents/${documentID}`,
    })

    handleAPIException<QueryDocumentDetailModel>(response)

    if (!response.data.detail.length) {
      errorMessage(getMessage('EF0002', ['ドキュメント']), 'error')
    }

    return response.data.detail[0]
  } catch (error) {
    console.log('queryDocumentDetail', error)
  }
}

/**
 * @description ファイルアップロード API
 * @param parameters
 * @returns ファイルパス
 */
export async function uploadFile({
  folderID,
  productID,
  documentID,
  fileName,
  fileContent,
}: UploadFileParameter): Promise<string> {
  try {
    const response: AxiosResponse<UploadFileModel> = await bffHTTP({
      method: 'POST',
      url: `/blobstorage`,
      data: {
        folder_id: folderID,
        product_id: productID,
        document_id: documentID,
        file_id: null,
        file_name: fileName,
        file_content: fileContent,
      },
    })

    handleAPIException<UploadFileModel>(response)
    return response.data[fileName]
  } catch (error) {
    console.log('uploadFile', error)
    return ''
  }
}

/**
 * @description ファイルダウンロード API
 * @param parameters ダウンロード用引数
 * @reutrns base64 にエンコードされたファイル
 */
export async function downloadFile(
  parameters: DownloadFileParameters | TransactionSequence,
  holdLoading?: boolean
): Promise<string> {
  try {
    const transactionSequence = parameters as TransactionSequence
    const _parameters = parameters as DownloadFileParameters

    const params =
      typeof transactionSequence === 'string'
        ? { transaction_sequence: transactionSequence }
        : {
            file_id: _parameters.fileID || null,
            file_path: encodeUnicodeToBase64(_parameters.filePath),
            product_id: _parameters.productID || null,
            folder_id: _parameters.folderID || null,
            document_id: _parameters.documentID || null,
            holdLoading: holdLoading
          }

    const response: AxiosResponse<string, any> = await bffHTTP({
      method: 'GET',
      url: '/blobstorage',
      params,
    })

    handleAPIException<string>(response)
    return response.data
  } catch (error) {
    console.log('downloadFile', error)
    return ''
  }
}

/**
 * @description 取引詳細画面用 ファイルダウンロード API
 * @param parameters ダウンロード用引数
 * @reutrns base64 にエンコードされたファイル
 */
export async function downloadFileTransaction(
  transaction_sequence: string,
  file_path: string
): Promise<string> {
  try {
    const params = { transaction_sequence: transaction_sequence, file_path: file_path }

    const response: AxiosResponse<string, any> = await bffHTTP({
      method: 'GET',
      url: '/blobstorage',
      params,
    })

    handleAPIException<string>(response)
    return response.data
  } catch (error) {
    console.log('downloadFile', error)
    return ''
  }
}

/**
 * @description ドキュメント詳細保存 API
 * @param parameters
 * @returns
 */
export async function saveDocumentDetail(
  { folderID, productID, documentList }: SaveDocumentDetailParameters,
  holdLoading?: boolean
): Promise<string> {
  try {
    const response: AxiosResponse<void, any> = await bffHTTP({
      method: 'PUT',
      url: '/products/documents',

      data: {
        folder_id: folderID,
        product_id: productID,
        document_list: documentList,
      },
      params: { holdLoading },
    })

    handleAPIException<void>(response)
    return getMessage('IF0001')
  } catch (error) {
    console.log('saveDocumentDetail', error)
    return ''
  }
}

// ドキュメント登録API
interface PostOkResponse {
  result: string
}

export async function queryPostDocumentAdd(
  postData: PostDocumentAddItemsType,
  holdLoading?: boolean
): Promise<PostOkResponse | undefined> {
  try {
    const response: AxiosResponse<QueryDocumentDetailModel, any> = await bffHTTP({
      method: 'POST',
      url: API.DOCUMENT_ADD,
      data: { 
        product_id: postData.product_id,
        folder_id: postData.folder_id,
        document_list: postData.document_list,
      },
      params: { holdLoading },
    })

    handleAPIException<QueryDocumentDetailModel>(response)
    return { result: 'ok' }
  } catch (error) {
    console.log('queryPostDocumentAdd', error)
  }
}

/**
 * @description 紐付商品取得 API
 * @param parameters 紐付商品取得用引数
 * @returns
 */
export async function queryProductDocumentRelation(
  parameters: QueryProductDocumentRelationParameters
): Promise<QueryProductDocumentRelationModel> {
  try {
    const response: AxiosResponse<{ detail: QueryProductDocumentRelationModel }, null> =
      await bffHTTP({
        url: '/product_document_relations',
        method: 'GET',
        params: {
          folder_id: parameters.folderID,
          product_id: parameters.productID,
          document_id: parameters.documentID,
        },
      })

    handleAPIException(response)

    return response.data.detail
  } catch (error) {
    console.error(error)
    return []
  }
}

/**
 * @description 紐付商品更新 API
 * @param parameters 紐付商品更新用引数
 * @returns
 */
export async function updateProductDocumentRelation(
  parameters: UpdateProductDocumentRelationParameters
): Promise<boolean> {
  try {
    const response: AxiosResponse<null, UpdateProductDocumentRelationParameters> =
      await bffHTTP(<AxiosRequestConfig<UpdateProductDocumentRelationParameters>>{
        url: '/product_document_relations',
        method: 'PUT',
        data: parameters,
      })

    handleAPIException(response)

    return response.status === 200
  } catch (error) {
    console.error(error)
    return false
  }
}

/**
 * @description OCRファイルデータ取込API
 * @param parameters
 * @returns ファイルパス
 */
export async function saveOCRFileData({
  folderID,
  productID,
  documentID,
  fileID,
  fileName,
  fileContent
}: SaveOcrFileDataParameter,
  holdLoading?: boolean
): Promise<string> {
  try {
    const response: AxiosResponse<void, any> = await bffHTTP({
      method: 'POST',
      url: `products/documents/ocr`,
      data: {
        folder_id: folderID,
        product_id: productID,
        document_id: documentID,
        file_id: fileID,
        file_name: fileName,
        file_content: fileContent
      },
      params: { holdLoading },
    })

    handleAPIException<void>(response)
    return getMessage('IF0001')
  } catch (error) {
    console.error(error)
    return ''
  }
}

/**
 * @description OCRファイルデータ取得API
 * @param parameters
 * @returns
 */
export async function getOCRFileData({
  productID,
  documentID
  }: GetOcrFileDataParameter
): Promise<QueryOCRFileListModel | undefined> {
  try {
    const response: AxiosResponse<QueryOCRFileListModel, any> = await bffHTTP({
      method: 'GET',
      url: `products/documents/ocr`,
      params: {
        product_id: productID,
        document_id: documentID,
      },
  })

    handleAPIException(response)

    return response.data
  } catch (error) {
    console.error(error)
    return 
  }
}

/**
 * @description OCRファイルデータ更新 API
 * @param parameters
 * @returns
 */
export async function updateOCRFileData({
  productID,
  documentID,
  fileID,
  fileName,
  folderID,
  OcrFileDataSaveDetail
}: UpdateOcrFileDataParameter,
  holdLoading?: boolean
): Promise<string> {
  try {
    const response: AxiosResponse<void, any> = await bffHTTP({
      method: 'PUT',
      url: 'products/documents/ocr',

      data: {
        folder_id: folderID,
        product_id: productID,
        document_id: documentID,
        file_id: fileID,
        file_name: fileName,
        ocr_file_data_save_detail: OcrFileDataSaveDetail,
      },
      params: { holdLoading },
    })

    handleAPIException<void>(response)
    return getMessage('IF0001')
  } catch (error) {
    return ''
  }
}